(() => {
  const BLOCK_SEL = '[data-phibra-media-slider="1"]';
  const SWIPER_SEL = '.phibra-media-slider__swiper';
  const INIT_KEY = '__phibraSwiperInstance';

  function waitForSwiper(cb, tries = 0) {
    if (window.Swiper) return cb();
    if (tries > 120) return; // ~6s
    setTimeout(() => waitForSwiper(cb, tries + 1), 50);
  }

  function isInEditor() {
    return (
      document.body.classList.contains('block-editor-page') ||
      !!document.querySelector('.block-editor-writing-flow')
    );
  }

  function isCoarsePointer() {
    return (
      (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) ||
      'ontouchstart' in window ||
      (navigator.maxTouchPoints || 0) > 0
    );
  }

  // ✅ "clamp" para o espaço entre slides em desktop:
  // - 40px em ~1024px (portáteis pequenos)
  // - 90px em ~1920px (ecrãs grandes)
  // - interpolação linear entre eles
  function calcSpaceBetween() {
    const w = Math.max(0, window.innerWidth || 0);

    const minW = 1024;
    const maxW = 1920;

    const minGap = 40;
    const maxGap = 90;

    if (w <= minW) return minGap;
    if (w >= maxW) return maxGap;

    const t = (w - minW) / (maxW - minW); // 0..1
    return Math.round(minGap + t * (maxGap - minGap));
  }

  const toBool = (v) => String(v ?? '').trim() === '1' || String(v ?? '').trim() === 'true';
  const toInt = (v, fallback) => {
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : fallback;
  };

  function stopAllVideos(blockEl) {
    blockEl.querySelectorAll('video').forEach((v) => {
      try { v.pause(); } catch (e) {}
    });
  }

  function playActiveVideo(swiper) {
    const activeSlide = swiper.slides?.[swiper.activeIndex];
    if (!activeSlide) return;

    swiper.el.querySelectorAll('video').forEach((v) => {
      try { v.pause(); } catch (e) {}
    });

    const v = activeSlide.querySelector('video');
    if (!v) return;

    v.muted = true;
    v.playsInline = true;
    v.loop = true;

    const tryPlay = () => v.play().catch(() => {});
    if (v.readyState >= 3) tryPlay();
    else {
      const onCanPlay = () => {
        v.removeEventListener('canplay', onCanPlay);
        tryPlay();
      };
      v.addEventListener('canplay', onCanPlay);
    }
  }

  function destroyIfAny(blockEl) {
    const inst = blockEl[INIT_KEY];
    if (inst && inst.destroy) {
      try { inst.destroy(true, true); } catch (e) {}
    }
    blockEl[INIT_KEY] = null;
    blockEl.removeAttribute('data-phibra-initialized');
  }

  function applyResponsiveSpace(swiper) {
    if (!swiper) return;

    const w = window.innerWidth || 0;

    // replica a tua lógica de breakpoints:
    // <575 -> base 24 (já fica no params)
    // >=575 e <1024 -> 40
    // >=1024 -> clamp 40..90
    let gap = 24;
    if (w >= 575 && w < 1024) gap = 40;
    else if (w >= 1024) gap = calcSpaceBetween();

    swiper.params.spaceBetween = gap;
    // garantir que Swiper recalcula os offsets/margens
    try { swiper.update(); } catch (e) {}
  }

  function initBlock(blockEl) {
    if (!blockEl) return;

    const sliderEl = blockEl.querySelector(SWIPER_SEL);
    if (!sliderEl) return;

    if (blockEl.getAttribute('data-phibra-initialized') === '1') {
      if (blockEl[INIT_KEY]?.el !== sliderEl) destroyIfAny(blockEl);
      else return;
    }

    if (!window.Swiper) return;

    const autoplayEnabled = toBool(blockEl.dataset.autoplay);
    const autoplayDelay = Math.max(0, toInt(blockEl.dataset.autoplayDelay, 3500));
    const loopEnabled = toBool(blockEl.dataset.loop);
    const speed = Math.max(50, toInt(blockEl.dataset.speed, 1000));

    const autoplayFinal = autoplayEnabled;
    const coarse = isCoarsePointer();

    const swiperConfig = {
      slidesPerView: 'auto',
      centeredSlides: true,
      spaceBetween: 24,

      // ✅ só em mobile/touch: permite scroll vertical por cima do swiper
      ...(coarse
        ? {
            touchStartPreventDefault: false,
            touchMoveStopPropagation: false,
          }
        : {}),

      speed,
      loop: loopEnabled,

      allowTouchMove: true,
      watchSlidesProgress: true,
      observer: true,
      observeParents: true,

      ...(autoplayFinal
        ? {
            autoplay: {
              delay: autoplayDelay,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            },
          }
        : {}),

      on: {
        init(sw) {
          sw.update();
          setTimeout(() => sw.update(), 50);

          // ✅ aplica o gap dinâmico no init
          applyResponsiveSpace(sw);

          if (autoplayFinal && sw.autoplay && typeof sw.autoplay.start === 'function') {
            try { sw.autoplay.start(); } catch (e) {}
          }

          playActiveVideo(sw);
        },
        slideChange(sw) {
          playActiveVideo(sw);
        },
      },

      breakpoints: {
        575: {
          slidesPerView: 'auto',
          spaceBetween: 40,
        },
        1024: {
          slidesPerView: 3,
          centeredSlides: true,
          // ✅ aqui em vez de fixo 90, usa o "clamp"
          spaceBetween: calcSpaceBetween(),
        },
      },
    };

    blockEl.setAttribute('data-phibra-initialized', '1');
    const swiper = new window.Swiper(sliderEl, swiperConfig);
    blockEl[INIT_KEY] = swiper;

    let isScrolling = false;
    sliderEl.addEventListener(
      'wheel',
      (event) => {
        const isHorizontal = Math.abs(event.deltaX) > Math.abs(event.deltaY);
        if (!isHorizontal) return;

        event.preventDefault();
        if (isScrolling || Math.abs(event.deltaX) < 30) return;

        isScrolling = true;

        if (event.deltaX > 0) swiper.slideNext();
        else swiper.slidePrev();

        setTimeout(() => (isScrolling = false), (swiper.params.speed || 0) + 100);
      },
      { passive: false }
    );

    if (isInEditor()) {
      setTimeout(() => {
        try { swiper.update(); } catch (e) {}
      }, 150);
    }
  }

  function initAll() {
    document.querySelectorAll(BLOCK_SEL).forEach((blockEl) => initBlock(blockEl));
  }

  function boot() {
    waitForSwiper(() => {
      initAll();

      // ✅ sempre que fizer boot, atualiza gap dos swipers existentes
      document.querySelectorAll(BLOCK_SEL).forEach((blockEl) => {
        const sw = blockEl[INIT_KEY];
        if (sw) applyResponsiveSpace(sw);
      });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  const mo = new MutationObserver(() => {
    boot();

    document.querySelectorAll(BLOCK_SEL).forEach((b) => {
      if (!document.contains(b)) {
        stopAllVideos(b);
        destroyIfAny(b);
      }
    });
  });

  mo.observe(document.documentElement, { childList: true, subtree: true });

  window.addEventListener('resize', () => boot());
})();